import { useState, useEffect, useCallback } from "react";

interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: "accepted" | "dismissed" }>;
}

interface InstallPromptState {
  isInstallable: boolean;
  isInstalled: boolean;
  showInstallPrompt: () => Promise<boolean>;
  dismissPrompt: () => void;
  shouldShowBanner: boolean;
}

const DISMISSED_KEY = "pwa-install-dismissed";
const DISMISSED_COUNT_KEY = "pwa-install-dismissed-count";
const LAST_DISMISSED_KEY = "pwa-install-last-dismissed";
const MAX_DISMISSALS = 3;
const DISMISS_COOLDOWN_DAYS = 7;

export function useInstallPrompt(): InstallPromptState {
  const [deferredPrompt, setDeferredPrompt] =
    useState<BeforeInstallPromptEvent | null>(null);
  const [isInstalled, setIsInstalled] = useState(false);
  const [shouldShowBanner, setShouldShowBanner] = useState(false);

  // Check if app is already installed
  const checkIfInstalled = useCallback(() => {
    // Check if running in standalone mode (installed PWA)
    const isStandalone = window.matchMedia(
      "(display-mode: standalone)"
    ).matches;
    const isIOSStandalone = (window.navigator as any).standalone === true;

    return isStandalone || isIOSStandalone;
  }, []);

  // Check if we should show the install banner
  const shouldShowInstallBanner = useCallback(() => {
    // Don't show if already installed
    if (checkIfInstalled()) {
      return false;
    }

    // Check localStorage for dismiss count and last dismissed date
    const dismissCount = parseInt(
      localStorage.getItem(DISMISSED_COUNT_KEY) || "0",
      10
    );
    const lastDismissed = localStorage.getItem(LAST_DISMISSED_KEY);

    // If dismissed max times, don't show
    if (dismissCount >= MAX_DISMISSALS) {
      return false;
    }

    // If dismissed recently, check cooldown period
    if (lastDismissed) {
      const lastDismissedDate = new Date(lastDismissed);
      const daysSinceLastDismiss =
        (Date.now() - lastDismissedDate.getTime()) / (1000 * 60 * 60 * 24);

      if (daysSinceLastDismiss < DISMISS_COOLDOWN_DAYS) {
        return false;
      }
    }

    return true;
  }, [checkIfInstalled]);

  // Show the install prompt
  const showInstallPrompt = useCallback(async (): Promise<boolean> => {
    if (!deferredPrompt) {
      console.log("Install prompt not available");
      return false;
    }

    try {
      // Show the install prompt
      await deferredPrompt.prompt();

      // Wait for the user's response
      const { outcome } = await deferredPrompt.userChoice;

      if (outcome === "accepted") {
        console.log("User accepted the install prompt");
        setIsInstalled(true);
        setShouldShowBanner(false);
        localStorage.removeItem(DISMISSED_KEY);
        localStorage.removeItem(DISMISSED_COUNT_KEY);
        localStorage.removeItem(LAST_DISMISSED_KEY);
        return true;
      } else {
        console.log("User dismissed the install prompt");
        dismissPrompt();
        return false;
      }
    } catch (error) {
      console.error("Error showing install prompt:", error);
      return false;
    } finally {
      // Clear the deferred prompt - it can only be used once
      setDeferredPrompt(null);
    }
  }, [deferredPrompt]);

  // Dismiss the install banner
  const dismissPrompt = useCallback(() => {
    const currentCount = parseInt(
      localStorage.getItem(DISMISSED_COUNT_KEY) || "0",
      10
    );
    localStorage.setItem(DISMISSED_COUNT_KEY, String(currentCount + 1));
    localStorage.setItem(LAST_DISMISSED_KEY, new Date().toISOString());
    localStorage.setItem(DISMISSED_KEY, "true");
    setShouldShowBanner(false);
  }, []);

  useEffect(() => {
    // Check if already installed
    setIsInstalled(checkIfInstalled());

    // Listen for the beforeinstallprompt event
    const handleBeforeInstallPrompt = (e: Event) => {
      // Prevent the default browser install prompt
      e.preventDefault();

      const promptEvent = e as BeforeInstallPromptEvent;
      setDeferredPrompt(promptEvent);

      // Check if we should show our custom banner
      if (shouldShowInstallBanner()) {
        setShouldShowBanner(true);
      }
    };

    // Listen for app installed event
    const handleAppInstalled = () => {
      console.log("PWA was installed");
      setIsInstalled(true);
      setShouldShowBanner(false);
      setDeferredPrompt(null);
    };

    // Add event listeners
    window.addEventListener("beforeinstallprompt", handleBeforeInstallPrompt);
    window.addEventListener("appinstalled", handleAppInstalled);

    // Cleanup
    return () => {
      window.removeEventListener(
        "beforeinstallprompt",
        handleBeforeInstallPrompt
      );
      window.removeEventListener("appinstalled", handleAppInstalled);
    };
  }, [checkIfInstalled, shouldShowInstallBanner]);

  return {
    isInstallable: deferredPrompt !== null,
    isInstalled,
    showInstallPrompt,
    dismissPrompt,
    shouldShowBanner,
  };
}
